import React, { useState, useRef } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { uploadLightReachDocument } from "../../dtos/lightreach";
import { CircularProgress } from "@material-ui/core";

const DOCUMENT_TYPES = [
  { value: "utilityBill", label: "Utility Bill" },
  { value: "voidedCheck", label: "Voided Check" },
  { value: "disclosure", label: "Disclosure" },
  { value: "supporting", label: "Supporting" },
];

/**
 * Modal for uploading documents to LightReach. Provides document type select
 * and file picker; submits via LightReach uploadDocument API.
 */
export default function UploadDocumentsModal({ show, onHide, acctId, onUploadSuccess }) {
  const [documentType, setDocumentType] = useState(DOCUMENT_TYPES[0].value);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleClose = () => {
    setDocumentType(DOCUMENT_TYPES[0].value);
    setFiles([]);
    setError(null);
    onHide();
  };

  const handleFileChange = (e) => {
    const selected = e.target.files;
    if (selected?.length) {
      setFiles(Array.from(selected));
    }
  };

  const handleUpload = async () => {
    if (!files.length) return;
    setError(null);
    setUploading(true);
    try {
      await uploadLightReachDocument(acctId, documentType, files);
      if (typeof onUploadSuccess === "function") {
        onUploadSuccess();
      }
      handleClose();
    } catch (err) {
      setError(err?.message || "Upload failed. Try again or contact Sales Support.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Upload Documents</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Document Type</Form.Label>
          <Form.Select
            value={documentType}
            onChange={(e) => setDocumentType(e.target.value)}
          >
            {DOCUMENT_TYPES.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-2">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
          >
            Choose files
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="d-none"
            onChange={handleFileChange}
          />
          {files.length > 0 && (
            <div className="small mt-2 text-muted">
              {files.length} file(s) selected
            </div>
          )}
        </Form.Group>
        {error && (
          <div className="text-danger small mt-2">{error}</div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={uploading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleUpload}
          disabled={uploading || files.length === 0 || !acctId}
        >
          {uploading ? (
            <>
              <CircularProgress size={16} color="inherit" className="me-1" />
              Uploading…
            </>
          ) : (
            "Upload Document"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
